/**
 * BucketService — puts one photo in one bucket. That is all it does.
 */

import type { BucketRepository, MediaRepository } from '../repositories';
import type { Bucket } from '../types';
import { noopRecorder, type ChangeSink, type DecisionRecorder } from './ports';

export interface BucketChange {
  mediaId: number;
  from: number | null;
  to: number | null;
}

export class BucketService {
  constructor(
    private readonly media: MediaRepository,
    private readonly buckets: BucketRepository,
    private readonly sink: ChangeSink,
    private readonly journal: DecisionRecorder = noopRecorder,
  ) {}

  list(projectId: number): Bucket[] {
    return this.buckets.list(projectId);
  }

  /** Assign, and hand back what changed so the caller can offer an undo. */
  assign(mediaId: number, bucketId: number | null): BucketChange | null {
    const item = this.media.byId(mediaId);
    if (!item) return null;
    if (item.bucketId === bucketId) return null;

    this.media.setBucket(mediaId, bucketId);
    // Journal first: this returns only once the decision is durable.
    this.journal.append({ t: 'bucket', id: mediaId, v: bucketId });
    this.sink.touch();
    return { mediaId, from: item.bucketId, to: bucketId };
  }

  /** Pressing a bucket's own hotkey again takes the photo back out of it. */
  toggle(mediaId: number, bucketId: number): BucketChange | null {
    const item = this.media.byId(mediaId);
    if (!item) return null;
    return this.assign(mediaId, item.bucketId === bucketId ? null : bucketId);
  }

  byHotkey(projectId: number, hotkey: string): Bucket | null {
    return this.buckets.byHotkey(projectId, hotkey);
  }

  /** Replays a change in reverse — used by undo. */
  revert(change: BucketChange): void {
    this.media.setBucket(change.mediaId, change.from);
    this.journal.append({ t: 'bucket', id: change.mediaId, v: change.from });
    this.sink.touch();
  }

  /** Replays a change forwards — used by redo. */
  reapply(change: BucketChange): void {
    this.media.setBucket(change.mediaId, change.to);
    this.journal.append({ t: 'bucket', id: change.mediaId, v: change.to });
    this.sink.touch();
  }
}
